import { useState } from 'react'
import { Alert, Box, Stack } from '@mui/material'
import { ArrowsClockwiseIcon, DownloadSimpleIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import {
    CardItem,
    FileDropArea,
    FileUploadCard,
    Modal,
    PageGridContainer,
    PageGridItem,
    TenantBreakdownTable,
    tokens,
    TOOLBAR_HEIGHT,
    type BreadcrumbItem,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { FeedbackTool, type FeedbackToolProps } from 'ComponentLibrary/src/molecules/FeedbackTool/FeedbackTool'
import { TextButtonNew, TinyButton } from 'ComponentLibrary/src/piazza/buttons'
import { isFileAboveMaxSize } from 'ComponentLibrary/src/common/files'
import { PlaygroundToolHeader, type PlaygroundToolHeaderCopy } from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolHeader'
import { PlaygroundToolPageContainer } from 'ComponentLibrary/src/playground/shared/layout/PlaygroundToolPageContainer'
import {
    EmptyPdfStateCard as EmptyStateCard,
    EmptyPdfStateContent as EmptyStateContent,
    EmptyPdfStateIllustration as EmptyStateIllustration,
} from 'ComponentLibrary/src/playground/shared/pdf/styled'
import { MAX_TENANT_IMPORT_BYTES, TENANT_IMPORT_FILE_TYPES } from '../workbook'
import { TenantImportClarifications } from '../TenantImportClarifications'
import { TenantImportPreview } from '../TenantImportPreview'
import { TenantImportProgress } from '../TenantImportProgress'
import { rowErrors } from '../review'
import { tenantImportExportLimitations } from '../excel'
import { tenantImportCopy } from '../copy'
import { germanTenantSummaryCopy } from '../tableCopy'
import type { TenantBreakdownTableCopy } from 'ComponentLibrary/src/widgets/TenantTables/TenantSummaryTables/TenantBreakdownTable'
import { tenantImportSummary } from './tenantImportSummary'
import { TenantImportFormatInfo, type TenantImportFormatCopy } from './TenantImportFormatInfo'
import { toPreviewTenant } from '../preview'
import type { TenantImportFlow } from '../useTenantImport'
import type { TenantDetailsTableCopy } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/TenantDetailsTable'

export type TenantImportToolCopy = {
    title: string
    infoText: string
    emptyDescription: string
    upload: string
    cancel: string
    replaceFile: string
    readyToDownload: string
    rowsLabel: (count: number) => string
    format: TenantImportFormatCopy
    exportLimitations: (count: number) => string
}

const DEFAULT_TENANT_IMPORT_TOOL_COPY: TenantImportToolCopy = {
    title: 'Mieterliste zu Excel',
    infoText: 'Lassen Sie Ihre Mieterliste auslesen, prüfen Sie das Ergebnis und laden Sie es im einwert-Excel-Format herunter.',
    emptyDescription: 'Ziehen Sie eine Mieterliste hierher oder wählen Sie eine Datei aus. Excel (.xlsx, .xlsm, .xls) oder PDF, bis 20 MB.',
    upload: 'Hochladen',
    cancel: 'Abbrechen',
    replaceFile: 'Datei ersetzen',
    readyToDownload: 'Bereit zum Download',
    rowsLabel: (count) => `${count} ${count === 1 ? 'Zeile' : 'Zeilen'}`,
    format: {
        title: 'einwert-Excel-Format',
        template:
            'Die Excel-Datei bildet die wichtigsten Mieterinformationen in einem einheitlichen Format ab, welches als Grundlage für Gutachten verwendet wird.',
        periods:
            'In diesem Format sind je Einheit bis zu vier Mietstaffeln, drei mietfreie Zeiten und drei Leerstandszeiten mit Start- und Enddatum möglich.',
        remarks:
            'Weitere Zeiträume und Leerstand ohne vollständige Datumsangaben stehen nur in der Bemerkung; fehlende Marktmieten bleiben leer.',
    },
    exportLimitations: tenantImportCopy().exportLimitations,
}

export function TenantImportTool({
    data,
    toolCopy = DEFAULT_TENANT_IMPORT_TOOL_COPY,
    headerCopy,
    feedbackButton,
    summaryCopy = germanTenantSummaryCopy,
    tableCopy,
    breadcrumbRoot,
}: {
    data: TenantImportFlow
    toolCopy?: TenantImportToolCopy
    headerCopy?: PlaygroundToolHeaderCopy
    feedbackButton?: FeedbackToolProps
    summaryCopy?: TenantBreakdownTableCopy
    tableCopy?: TenantDetailsTableCopy
    breadcrumbRoot?: BreadcrumbItem
}) {
    const { copy, language } = data
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
    const [modalSelectedFile, setModalSelectedFile] = useState<File>()
    const [isOver, setIsOver] = useState(false)
    const [fileError, setFileError] = useState<string>()
    const busy = data.step === 'progress' || data.saving
    const showResult = data.step === 'review' || data.step === 'done'
    const exportLimitationsCount = data.rows.filter((row) => tenantImportExportLimitations(row).length > 0).length
    const validationFailure = data.result?.failure_reason || data.rows.flatMap(rowErrors).at(0)
    const importBlocked = data.result && !data.result.ready && !data.result.questions.length
    const summary = tenantImportSummary(data.rows.map(toPreviewTenant))
    const downloadTitle = importBlocked ? copy.blocked : toolCopy.readyToDownload
    const uploadFile = (file?: File) => {
        if (!file || busy) {
            return
        }
        if (isFileAboveMaxSize(file, MAX_TENANT_IMPORT_BYTES)) {
            setFileError(copy.fileSizeError)
            return
        }
        setFileError(undefined)
        void data.upload(file)
    }
    const openUploadModal = () => {
        if (busy) {
            return
        }
        setModalSelectedFile(undefined)
        setIsUploadModalOpen(true)
    }
    const closeUploadModal = () => {
        setModalSelectedFile(undefined)
        setIsUploadModalOpen(false)
    }
    const confirmUpload = () => {
        uploadFile(modalSelectedFile)
        closeUploadModal()
    }

    return (
        <PlaygroundToolPageContainer
            sx={{
                maxWidth: 'none',
                minWidth: 0,
                margin: 0,
                gap: 0,
                paddingBottom: tokens.SizingSpacing200,
                minHeight: `calc(100vh - ${TOOLBAR_HEIGHT} - 1px)`,
                height: data.step === 'progress' ? `calc(100vh - ${TOOLBAR_HEIGHT} - 1px)` : undefined,
                backgroundColor: tokens.ColorBackgroundDark,
            }}
        >
            <PlaygroundToolHeader
                title={toolCopy.title}
                currentLocation="/playground/tenant-list-import"
                breadcrumbRoot={breadcrumbRoot}
                copy={headerCopy}
                infoText={toolCopy.infoText}
                actions={
                    <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing150} flexWrap="wrap">
                        {feedbackButton ? <FeedbackTool {...feedbackButton} size="tiny" /> : null}
                        {data.file ? (
                            <TinyButton
                                label={toolCopy.replaceFile}
                                leftIcon={<ArrowsClockwiseIcon />}
                                onClick={openUploadModal}
                                disabled={busy}
                            />
                        ) : (
                            <TextButtonNew
                                label={copy.upload}
                                leftIcon={<UploadSimpleIcon />}
                                onClick={openUploadModal}
                                variant="primary"
                            />
                        )}
                    </Stack>
                }
            />
            <Modal
                fullWidth
                width="600px"
                showCloseIcon
                closeIconTooltip={copy.done}
                isOpen={isUploadModalOpen}
                onClose={closeUploadModal}
                title={data.file ? toolCopy.replaceFile : copy.upload}
                footerProps={{
                    primaryButton: {
                        text: toolCopy.upload,
                        icon: <UploadSimpleIcon />,
                        disabled: !modalSelectedFile,
                        onClick: confirmUpload,
                    },
                    secondaryButton: {
                        text: toolCopy.cancel,
                        onClick: closeUploadModal,
                    },
                }}
            >
                <FileUploadCard
                    title={copy.file}
                    description={copy.uploadDescription}
                    file={modalSelectedFile ?? data.file}
                    setFile={setModalSelectedFile}
                    allowedFileTypes={TENANT_IMPORT_FILE_TYPES}
                    maxFileSizeBytes={MAX_TENANT_IMPORT_BYTES}
                    fileSizeErrorMessage={copy.fileSizeError}
                />
            </Modal>
            <Stack
                gap={showResult ? tokens.SizingSpacing400 : tokens.SizingSpacing200}
                sx={{
                    padding: showResult ? `${tokens.SizingSpacing200} 0 0` : tokens.SizingSpacing200,
                    flex: 1,
                    minWidth: 0,
                    minHeight: data.step === 'progress' ? 0 : undefined,
                    overflowY: data.step === 'progress' ? 'auto' : undefined,
                }}
            >
                {(!showResult || !data.result) && (fileError || data.error) && (
                    <Alert severity="warning" sx={{ width: '100%', maxWidth: '720px', margin: '0 auto' }}>
                        {fileError || data.error}
                    </Alert>
                )}
                {data.step === 'file' && (
                    <Stack flex={1} justifyContent="center" alignItems="center">
                        <FileDropArea
                            allowedFileTypes={TENANT_IMPORT_FILE_TYPES}
                            maxFileSizeBytes={MAX_TENANT_IMPORT_BYTES}
                            fileSizeErrorMessage={copy.fileSizeError}
                            onFileValidationError={setFileError}
                            setIsOver={setIsOver}
                            onDataDrop={(_areaId, files) => uploadFile(files?.[0])}
                            width="100%"
                        >
                            <EmptyStateCard
                                sx={{
                                    width: '100%',
                                    maxWidth: '532px',
                                    margin: '0 auto',
                                    backgroundColor: isOver ? tokens.ColorSurfaceHover : tokens.ColorSurfaceDefault,
                                }}
                            >
                                <EmptyStateContent>
                                    <EmptyStateIllustration width="270px" height="270px" />
                                    <Typography variant="headline2" color={tokens.ColorTextBoldest}>
                                        {copy.upload}
                                    </Typography>
                                    <Typography variant="textM" color={tokens.ColorTextDefault}>
                                        {toolCopy.emptyDescription}
                                    </Typography>
                                    <Stack direction="row" gap={tokens.SizingSpacing150}>
                                        <TinyButton label={copy.upload} leftIcon={<UploadSimpleIcon />} onClick={openUploadModal} />
                                    </Stack>
                                </EmptyStateContent>
                            </EmptyStateCard>
                        </FileDropArea>
                    </Stack>
                )}
                {data.step === 'progress' && (
                    <Stack
                        flex={1}
                        minHeight={0}
                        alignItems="center"
                        sx={{
                            // Twice the offset below shifts the center up; this space shrinks on short screens.
                            '&::before': { content: '""', flex: '1 1 0px' },
                            '&::after': { content: '""', flex: `1 1 calc(${tokens.SizingSpacing1000} * 2)` },
                        }}
                    >
                        <Stack gap={tokens.SizingSpacing200} sx={{ width: '100%', maxWidth: '720px', flexShrink: 0 }}>
                            <TenantImportProgress language={language} progress={data.progress} isFollowUp={!!data.result} />
                        </Stack>
                    </Stack>
                )}
                {showResult && data.result && (
                    <>
                        <PageGridContainer>
                            <PageGridItem columnSpan={6} columnSpanXs={4}>
                                <CardItem
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: `minmax(0, 1fr) ${tokens.SizingBorderS} minmax(0, 1fr)`,
                                        gap: tokens.SizingSpacing300,
                                        '@media (max-width: 1023px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
                                    }}
                                >
                                    <TenantImportFormatInfo copy={toolCopy.format} />
                                    <Box
                                        sx={{
                                            backgroundColor: tokens.ColorBorderDefault,
                                            '@media (max-width: 1023px)': { height: tokens.SizingBorderS },
                                        }}
                                    />
                                    <Stack
                                        component="section"
                                        aria-label={data.result.questions.length ? undefined : downloadTitle}
                                        gap={tokens.SizingSpacing200}
                                        minWidth={0}
                                    >
                                        {data.result.questions.length ? (
                                            <TenantImportClarifications
                                                embedded
                                                language={language}
                                                questions={data.result.questions}
                                                rows={data.rows}
                                                answers={data.answers}
                                                onChange={data.setAnswers}
                                                onComplete={(answers) => void data.analyze(answers)}
                                                onRestart={() => data.setFile()}
                                                disabled={data.saving}
                                            />
                                        ) : (
                                            <>
                                                <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                                                    {downloadTitle}
                                                </Typography>
                                                <Stack direction="row">
                                                    <TextButtonNew
                                                        label={data.saving ? copy.downloading : copy.download}
                                                        leftIcon={<DownloadSimpleIcon />}
                                                        variant="primary"
                                                        onClick={() => void data.complete()}
                                                        disabled={!data.canComplete || data.saving}
                                                    />
                                                </Stack>
                                            </>
                                        )}
                                        {(fileError || data.error) && <Alert severity="warning">{fileError || data.error}</Alert>}
                                        {importBlocked && !data.error && (
                                            <Alert severity="error">
                                                {validationFailure ? `${copy.blocked}: ${validationFailure}` : copy.invalid}
                                            </Alert>
                                        )}
                                        {exportLimitationsCount > 0 && (
                                            <Alert severity="warning">{toolCopy.exportLimitations(exportLimitationsCount)}</Alert>
                                        )}
                                    </Stack>
                                </CardItem>
                            </PageGridItem>
                        </PageGridContainer>
                        {!!data.rows.length && (
                            <TenantImportPreview
                                rows={data.rows}
                                initialView="detail"
                                pageLayout={{ rowsLabel: toolCopy.rowsLabel, summaryRowCount: summary.length }}
                                structures={[]}
                                language={language}
                                tableCopy={tableCopy}
                                maxHeight="max(320px, calc(100vh - 320px))"
                                renderSummary={() => (
                                    <TenantBreakdownTable
                                        data={summary}
                                        copy={summaryCopy}
                                        locale={language === 'en' ? 'en-GB' : 'de-DE'}
                                    />
                                )}
                            />
                        )}
                    </>
                )}
            </Stack>
        </PlaygroundToolPageContainer>
    )
}
